import React from "react";
import { motion } from "framer-motion";
import { ABOUT_IMAGE } from "@/lib/site";
import { Sparkles, Compass, ShieldCheck } from "lucide-react";

const pillars = [
  {
    icon: Sparkles,
    title: "Dignity-first",
    body: "We meet every mother where she is — with respect, privacy and warmth.",
  },
  {
    icon: Compass,
    title: "Community-led",
    body: "Programs are designed with mothers, not for them. Their voice steers the work.",
  },
  {
    icon: ShieldCheck,
    title: "Long-term care",
    body: "We invest in stability — not handouts. Education, livelihoods, healing that lasts.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 lg:py-28 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            className="lg:col-span-5 relative"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="relative aspect-[4/5] rounded-[24px] overflow-hidden ring-1 ring-brand-border">
              <img
                src={ABOUT_IMAGE}
                alt="A mother and her child in a tender, hopeful moment"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-brand-forest text-white rounded-2xl px-6 py-5 max-w-[240px]">
              <div className="font-serif text-2xl leading-tight">
                “Nguvu Ya Mwanamke”
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-brand-gold mt-2">
                Swahili — “The Power of a Woman”
              </div>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <div className="text-xs uppercase tracking-[0.22em] text-brand-forest font-semibold">
              About the Foundation
            </div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-[1.05] tracking-tight">
              Born from{" "}
              <span className="italic text-brand-forest">lived experience.</span>{" "}
              Built for every mother who refuses to be invisible.
            </h2>
            <p className="mt-6 text-brand-muted text-lg leading-relaxed max-w-2xl">
              Nguvu Ya Mwanamke Foundation supports single mothers — and
              especially those raising children with disabilities — by
              connecting them with emotional support, education, economic
              opportunities, healthcare and community.
            </p>
            <p className="mt-4 text-brand-muted text-lg leading-relaxed max-w-2xl">
              We exist because no mother should have to choose between her
              healing and her child&apos;s future.
            </p>

            <div className="mt-10 grid sm:grid-cols-3 gap-5">
              {pillars.map((p) => (
                <div
                  key={p.title}
                  data-testid={`about-pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl border border-brand-border bg-white/70 backdrop-blur p-5 hover-lift"
                >
                  <div className="h-10 w-10 rounded-xl bg-brand-forest/10 flex items-center justify-center text-brand-forest">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-serif text-lg text-brand-ink">
                    {p.title}
                  </div>
                  <p className="mt-1 text-sm text-brand-muted leading-relaxed">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
